/* prettier-ignore-file */
/**
 * Wizard step 1 — rental dates.
 *
 * Shows how many of each Product1 item is available for the currently selected
 * range, and a per-day total in each calendar cell, so the customer can shift
 * dates to find availability before committing to items.
 *
 * Calendar behaviour is adapted from NewBooking.js:3121-3197.
 */

import React, { useMemo } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { computeMonthAvailability, toDayKey } from "../../utils/rentalAvailability";
import { getRentalDays } from "../../utils/rentalPricing";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const StepDates = ({
  startDate,
  endDate,
  currentMonth,
  onSelectDate,
  onMonthChange,
  bookings,
  pendingBookings,
  holdTick,
  products1,
  product1Title,
  availability,
  isAuthenticated,
  isDesktop,
}) => {
  const monthAvailability = useMemo(
    () =>
      computeMonthAvailability({
        month: currentMonth,
        bookings,
        pendingBookings,
        products1Catalog: products1,
      }),
    // holdTick: soft holds expire with the clock, so this must re-derive over time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentMonth, bookings, pendingBookings, products1, holdTick]
  );

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(year, month, day));
    return cells;
  }, [currentMonth]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const isInRange = (date) => {
    if (!startDate || !endDate || !date) return false;
    return date > startDate && date < endDate;
  };

  const days = getRentalDays(startDate, endDate);

  const formatShort = (date) =>
    date
      ? date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
      : "—";

  const trackedProducts = (products1 || []).filter((p) => Number(p?.quantity) > 0);

  return (
    <ScrollView style={styles.container}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Select your rental dates</Text>

        <View style={styles.monthNav}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.monthButton}
            onPress={() => onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
          >
            <Text style={styles.monthButtonText}>‹</Text>
          </TouchableOpacity>
          <Text style={styles.monthTitle}>
            {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </Text>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.monthButton}
            onPress={() => onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
          >
            <Text style={styles.monthButtonText}>›</Text>
          </TouchableOpacity>
        </View>

        {/* Cells are square and 1/7 of the container, so on desktop the grid is
            capped rather than the cells restyled — keeps full-size touch targets
            on phone, where most customers are. */}
        <View style={isDesktop ? styles.calendarBodyDesktop : null}>
        <View style={styles.weekdayRow}>
          {WEEKDAYS.map((day) => (
            <Text key={day} style={styles.weekdayText}>
              {day}
            </Text>
          ))}
        </View>

        <View style={styles.calendarGrid}>
          {calendarDays.map((date, index) => {
            if (!date) return <View key={`pad-${index}`} style={styles.dayCell} />;

            const isPast = date < today;
            // Unauthenticated customers cannot book same-day (NewBooking.js:3161).
            const isTodayBlocked = !isAuthenticated && isSameDay(date, today);
            const disabled = isPast || isTodayBlocked;

            const isStart = isSameDay(date, startDate);
            const isEnd = isSameDay(date, endDate);
            const isToday = isSameDay(date, today);
            const inRange = isInRange(date);
            const dayInfo = monthAvailability[toDayKey(date)];
            const soldOut = dayInfo && trackedProducts.length > 0 && dayInfo.totalAvailable === 0;

            return (
              <TouchableOpacity
                key={date.toISOString()}
                style={[
                  styles.dayCell,
                  inRange && styles.dayCellInRange,
                  (isStart || isEnd) && styles.dayCellSelected,
                  isToday && !(isStart || isEnd) && styles.dayCellToday,
                  disabled && styles.dayCellDisabled,
                ]}
                onPress={() => !disabled && onSelectDate(date)}
                disabled={disabled}
              >
                <Text
                  style={[
                    styles.dayText,
                    (isStart || isEnd) && styles.dayTextSelected,
                    disabled && styles.dayTextDisabled,
                  ]}
                >
                  {date.getDate()}
                </Text>
                {!disabled && dayInfo && trackedProducts.length > 0 && (
                  <Text
                    style={[
                      styles.dayAvailability,
                      (isStart || isEnd) && styles.dayAvailabilitySelected,
                      soldOut && styles.dayAvailabilitySoldOut,
                    ]}
                  >
                    {dayInfo.totalAvailable}
                  </Text>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        </View>

        <Text style={styles.calendarLegend}>
          The small number is how many {product1Title.toLowerCase()} are free that day.
        </Text>
      </View>

      {/* Selected range summary */}
      <View style={styles.section}>
        <View style={styles.rangeRow}>
          <View style={styles.rangeBlock}>
            <Text style={styles.rangeLabel}>Start</Text>
            <Text style={[styles.rangeValue, { color: "#1F8A5B" }]}>{formatShort(startDate)}</Text>
          </View>
          <View style={styles.rangeBlock}>
            <Text style={styles.rangeLabel}>End</Text>
            <Text style={[styles.rangeValue, { color: "#C4453D" }]}>{formatShort(endDate)}</Text>
          </View>
          <View style={styles.rangeBlock}>
            <Text style={styles.rangeLabel}>Length</Text>
            <Text style={styles.rangeValue}>{days > 0 ? `${days} ${days === 1 ? "day" : "days"}` : "—"}</Text>
          </View>
        </View>

        {(!startDate || !endDate) && (
          <Text style={styles.warningText}>
            ⚠️ Pick a start and an end date. Minimum rental is 1 full day, so they must be different days.
          </Text>
        )}
      </View>

      {/* Availability for the chosen range */}
      {startDate && endDate && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Available for these dates</Text>
          {trackedProducts.length === 0 ? (
            <Text style={styles.emptyText}>No inventory-tracked {product1Title.toLowerCase()} configured.</Text>
          ) : (
            trackedProducts.map((product) => {
              const info = availability?.product1?.[product.id];
              const available = info?.available ?? 0;
              const total = info?.total ?? product.quantity;
              return (
                <View key={product.id} style={styles.availabilityRow}>
                  <Text style={styles.availabilityName}>{product.name}</Text>
                  <View style={styles.availabilityRight}>
                    <Text
                      style={[
                        styles.availabilityCount,
                        available === 0 && styles.availabilityNone,
                        available > 0 && styles.availabilitySome,
                      ]}
                    >
                      {available === 0 ? "None left" : `${available} of ${total} available`}
                    </Text>
                    {/* A hold is someone mid-checkout, not a sale. It frees itself. */}
                    {info?.held > 0 && (
                      <Text style={styles.availabilityHeld}>{info.held} held in checkout</Text>
                    )}
                  </View>
                </View>
              );
            })
          )}
          <Text style={styles.availabilityHint}>
            Counts reflect the whole range — an item is only listed as available if it is free every day.
            Shift your dates to see other options.
          </Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  section: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 16, fontWeight: "bold", color: "#2E3A38", marginBottom: 12 },
  emptyText: { fontSize: 13, color: "#8A9794" },

  monthNav: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  monthButton: { paddingHorizontal: 16, paddingVertical: 6, cursor: "pointer" },
  monthButtonText: { fontSize: 24, color: "#0E7C6B", lineHeight: 26 },
  monthTitle: { fontSize: 16, fontWeight: "600", color: "#2E3A38" },

  weekdayRow: { flexDirection: "row" },
  weekdayText: { flex: 1, textAlign: "center", fontSize: 11, color: "#8A9794", paddingBottom: 6 },

  // Capping the grid width, not the cell, keeps the 1/7-width square geometry intact.
  calendarBodyDesktop: { maxWidth: 340, alignSelf: "center", width: "100%" },
  calendarGrid: { flexDirection: "row", flexWrap: "wrap" },
  dayCell: {
    width: `${100 / 7}%`,
    aspectRatio: 1,
    maxHeight: 54,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    cursor: "pointer",
  },
  dayCellInRange: { backgroundColor: "#E3F2EF" },
  dayCellSelected: { backgroundColor: "#0E7C6B" },
  dayCellToday: { borderWidth: 2, borderColor: "#E57373" },
  dayCellDisabled: { cursor: "default" },
  dayText: { fontSize: 14, color: "#2E3A38" },
  dayTextSelected: { color: "#FFFFFF", fontWeight: "bold" },
  dayTextDisabled: { color: "#DDE3E2" },
  dayAvailability: { fontSize: 9, color: "#1F8A5B", marginTop: 1 },
  dayAvailabilitySelected: { color: "#FFFFFF" },
  dayAvailabilitySoldOut: { color: "#C4453D" },
  calendarLegend: { fontSize: 11, color: "#8A9794", marginTop: 10, textAlign: "center" },

  rangeRow: { flexDirection: "row", justifyContent: "space-between" },
  rangeBlock: { flex: 1 },
  rangeLabel: { fontSize: 11, color: "#8A9794", marginBottom: 2 },
  rangeValue: { fontSize: 14, fontWeight: "600", color: "#2E3A38" },
  warningText: { fontSize: 12, color: "#B4761B", marginTop: 12 },

  availabilityRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  availabilityName: { fontSize: 14, color: "#2E3A38", flex: 1, paddingRight: 8 },
  availabilityRight: { alignItems: "flex-end" },
  availabilityCount: { fontSize: 13, fontWeight: "600" },
  availabilityHeld: { fontSize: 11, color: "#B4761B", marginTop: 2 },
  availabilitySome: { color: "#1F8A5B" },
  availabilityNone: { color: "#C4453D" },
  availabilityHint: { fontSize: 11, color: "#8A9794", marginTop: 10 },
});

export default StepDates;
